import * as Sentry from '@sentry/react';

import type { Geometry, GeometryInput } from 'src/types/__generated__/graphql';
import type { ExtendedAdsQueryVariables } from '../types/extendedAdsQueryVariables';
import { getIntersectionGeometriesFromGeometryInputs } from 'src/utils/googleMapUtils';

type Params = {
    queryVariables: ExtendedAdsQueryVariables;
    mapBounds?: Geometry;
};

export const getIntersectionFromQueryVariablesAndMapBounds = ({ queryVariables, mapBounds }: Params) => {
    if (!mapBounds || !queryVariables.places?.length || !queryVariables.places[0].coordinates) {
        return queryVariables;
    }

    try {
        const intersectionPolygonGeometries = getIntersectionGeometriesFromGeometryInputs(queryVariables.places, [
            mapBounds,
        ] as GeometryInput[]);

        if (!intersectionPolygonGeometries?.length) {
            return {
                ...queryVariables,
                noIntersection: true,
            };
        }

        return {
            ...queryVariables,
            places: intersectionPolygonGeometries,
        };
    } catch (err: unknown) {
        const error = err as Error;
        const errorDetails = JSON.stringify({
            name: error.name,
            message: error.message,
            stack: error.stack,
            values: {
                variablesPlaces: queryVariables.places,
                mapBounds,
            },
        });
        // If the intersection calculation fails, we don't want to break the search
        // so we just log the error and return the queryVariables as they are, without the intersection
        Sentry.captureException(
            new Error(
                `Error while calculating the intersection between variables places and map bounds: ${errorDetails}`,
            ),
        );
        return queryVariables;
    }
};
